import { c as _c } from "react/compiler-runtime";
import { Badge } from "@osmix/ui/badge";
import { Button } from "@osmix/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@osmix/ui/dialog";
import { Input } from "@osmix/ui/input";
import { Label } from "@osmix/ui/label";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, type EmailSendOutcome } from "@/api";
import { emailKeys, useEmailLog, useEmailMessage, useEmailPreview, useEmailStatus, useEmailTemplates } from "@/api/hooks/use-email";
import { hasAdminPermission } from "@/lib/admin-permissions";
import { formatDateTime } from "@/lib/utils";
import { useAuth } from "@/store";
const STATUS_FILTERS = ["all", "queued", "sent", "delivered", "failed", "skipped", "suppressed"] as const;
const statusVariant = (status: string): "default" | "secondary" | "destructive" | "outline" => status === "sent" || status === "delivered" ? "default" : status === "failed" ? "destructive" : status === "skipped" || status === "suppressed" ? "outline" : "secondary";
const modeVariant = (mode: string): "default" | "secondary" | "destructive" | "outline" => mode === "live" ? "default" : mode === "not_configured" ? "destructive" : "secondary";

/** Предпросмотр письма: изолированный iframe, чтобы стили письма не протекали в консоль. */
function EmailFrame(t0) {
  const $ = _c(3);
  const {
    html,
    title
  } = t0;
  let t1;
  if ($[0] !== html || $[1] !== title) {
    t1 = <iframe title={title} srcDoc={html} sandbox="" className="h-[640px] w-full rounded-md border bg-[#F8F8F8]" />;
    $[0] = html;
    $[1] = title;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}

/**
 * «Уведомления → Почта».
 *
 * Отвечает на вопросы, которые возникают после запуска писем: подключён ли
 * сервис и каких переменных не хватает, не упёрлись ли в суточный лимит Яндекса,
 * ушло ли письмо конкретному человеку и почему нет. Здесь же — тестовое письмо
 * на любой адрес и предпросмотр каждого шаблона на образце.
 */
export function EmailPage() {
  const $ = _c(402);
  const {
    t
  } = useTranslation();
  const queryClient = useQueryClient();
  const user = useAuth(_temp);
  let t0;
  if ($[0] !== user) {
    t0 = hasAdminPermission(user, "action.integrations.manage");
    $[0] = user;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const canManage = t0;
  const {
    data: status,
    error: statusError
  } = useEmailStatus();
  const [toFilter, setToFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [templateFilter, setTemplateFilter] = useState("all");
  let t1;
  if ($[2] !== statusFilter || $[3] !== templateFilter || $[4] !== toFilter) {
    t1 = {
      to: toFilter,
      status: statusFilter,
      template: templateFilter,
      limit: 50
    };
    $[2] = statusFilter;
    $[3] = templateFilter;
    $[4] = toFilter;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const {
    data: log
  } = useEmailLog(t1);
  const {
    data: templates
  } = useEmailTemplates();
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [openMessage, setOpenMessage] = useState(null);
  const [previewId, setPreviewId] = useState(null);
  const [previewAsText, setPreviewAsText] = useState(false);
  const {
    data: preview
  } = useEmailPreview(previewId);
  const {
    data: message
  } = useEmailMessage(openMessage);
  let t2;
  let t3;
  if ($[6] !== previewId || $[7] !== templates) {
    t2 = () => {
      if (!previewId && templates?.[0]) {
        setPreviewId(templates[0].id);
      }
    };
    t3 = [templates, previewId];
    $[6] = previewId;
    $[7] = templates;
    $[8] = t2;
    $[9] = t3;
  } else {
    t2 = $[8];
    t3 = $[9];
  }
  useEffect(t2, t3);
  let t4;
  if ($[10] !== queryClient) {
    const reload = () => {
      queryClient.invalidateQueries({
        queryKey: emailKeys.status
      });
      queryClient.invalidateQueries({
        queryKey: ["email", "log"]
      });
    };
    t4 = async work => {
      setActionError(null);
      ;
      try {
        await work();
        reload();
      } catch (t5) {
        const e = t5;
        setActionError(e instanceof Error ? e.message : String(e));
      }
    };
    $[10] = queryClient;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const act = t4;
  let t5;
  if ($[12] !== act || $[13] !== testTo) {
    t5 = async () => {
      setTesting(true);
      setTestResult(null);
      await act(async () => setTestResult(await api.email.test(testTo.trim())));
      setTesting(false);
    };
    $[12] = act;
    $[13] = testTo;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const runTest = t5;
  if (statusError) {
    let t6;
    if ($[15] !== statusError.message) {
      t6 = <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">{statusError.message}</div>;
      $[15] = statusError.message;
      $[16] = t6;
    } else {
      t6 = $[16];
    }
    return t6;
  }
  if (!status) {
    let t6;
    if ($[17] !== t) {
      t6 = t("email.loading");
      $[17] = t;
      $[18] = t6;
    } else {
      t6 = $[18];
    }
    let t7;
    if ($[19] !== t6) {
      t7 = <p className="text-muted-foreground">{t6}</p>;
      $[19] = t6;
      $[20] = t7;
    } else {
      t7 = $[20];
    }
    return t7;
  }
  let t6;
  if ($[21] !== t) {
    t6 = t("email.stats.total");
    $[21] = t;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== status.stats.total || $[24] !== t6) {
    t7 = {
      label: t6,
      value: status.stats.total
    };
    $[23] = status.stats.total;
    $[24] = t6;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== t) {
    t8 = t("email.stats.sent");
    $[26] = t;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  const t9 = status.stats.sent + status.stats.delivered;
  let t10;
  if ($[28] !== t8 || $[29] !== t9) {
    t10 = {
      label: t8,
      value: t9
    };
    $[28] = t8;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== t) {
    t11 = t("email.stats.queued");
    $[31] = t;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== status.stats.queued || $[34] !== t11) {
    t12 = {
      label: t11,
      value: status.stats.queued
    };
    $[33] = status.stats.queued;
    $[34] = t11;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== t) {
    t13 = t("email.stats.failed");
    $[36] = t;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  let t14;
  if ($[38] !== status.stats.failed || $[39] !== t13) {
    t14 = {
      label: t13,
      value: status.stats.failed
    };
    $[38] = status.stats.failed;
    $[39] = t13;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== t) {
    t15 = t("email.stats.skipped");
    $[41] = t;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== status.stats.skipped || $[44] !== t15) {
    t16 = {
      label: t15,
      value: status.stats.skipped
    };
    $[43] = status.stats.skipped;
    $[44] = t15;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  let t17;
  if ($[46] !== t10 || $[47] !== t12 || $[48] !== t14 || $[49] !== t16 || $[50] !== t7) {
    t17 = [t7, t10, t12, t14, t16];
    $[46] = t10;
    $[47] = t12;
    $[48] = t14;
    $[49] = t16;
    $[50] = t7;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  const stats = t17;
  let t18;
  if ($[52] !== status.dailyLimit || $[53] !== status.sentLast24h) {
    t18 = status.dailyLimit > 0 ? Math.min(100, Math.round(status.sentLast24h / status.dailyLimit * 100)) : 0;
    $[52] = status.dailyLimit;
    $[53] = status.sentLast24h;
    $[54] = t18;
  } else {
    t18 = $[54];
  }
  const usage = t18;
  let t19;
  let t20;
  let t21;
  let t22;
  let t23;
  let t24;
  let t25;
  let t26;
  let t27;
  let t28;
  let t29;
  if ($[55] !== act || $[56] !== actionError || $[57] !== canManage || $[58] !== previewAsText || $[59] !== previewId || $[60] !== runTest || $[61] !== stats || $[62] !== status.apiUrl || $[63] !== status.configErrors || $[64] !== status.dailyLimit || $[65] !== status.from || $[66] !== status.fromName || $[67] !== status.live || $[68] !== status.missingConfig || $[69] !== status.mode || $[70] !== status.note || $[71] !== status.pauseReason || $[72] !== status.pausedUntil || $[73] !== status.provider || $[74] !== status.sentLast24h || $[75] !== status.smtpHost || $[76] !== status.smtpPort || $[77] !== status.stats.windowHours || $[78] !== status.variables || $[79] !== status.webhookUrl || $[80] !== t || $[81] !== templates || $[82] !== testResult || $[83] !== testTo || $[84] !== testing || $[85] !== usage) {
    const grouped = (["seller", "buyer", "admin"] as const).map(audience => ({
      audience,
      items: (templates ?? []).filter(tpl => tpl.audience === audience)
    }));
    t25 = "flex flex-col gap-6";
    let t30;
    if ($[97] !== t) {
      t30 = t("email.breadcrumb");
      $[97] = t;
      $[98] = t30;
    } else {
      t30 = $[98];
    }
    let t31;
    if ($[99] !== t30) {
      t31 = <p className="text-xs uppercase text-muted-foreground">{t30}</p>;
      $[99] = t30;
      $[100] = t31;
    } else {
      t31 = $[100];
    }
    let t32;
    if ($[101] !== t) {
      t32 = t("nav.email");
      $[101] = t;
      $[102] = t32;
    } else {
      t32 = $[102];
    }
    let t33;
    if ($[103] !== t32) {
      t33 = <h1 className="text-2xl font-bold">{t32}</h1>;
      $[103] = t32;
      $[104] = t33;
    } else {
      t33 = $[104];
    }
    let t34;
    if ($[105] !== t) {
      t34 = t("email.intro");
      $[105] = t;
      $[106] = t34;
    } else {
      t34 = $[106];
    }
    let t35;
    if ($[107] !== t34) {
      t35 = <p className="text-sm text-muted-foreground">{t34}</p>;
      $[107] = t34;
      $[108] = t35;
    } else {
      t35 = $[108];
    }
    if ($[109] !== t31 || $[110] !== t33 || $[111] !== t35) {
      t26 = <div className="flex flex-col gap-1" data-tour="email-intro">{t31}{t33}{t35}</div>;
      $[109] = t31;
      $[110] = t33;
      $[111] = t35;
      $[112] = t26;
    } else {
      t26 = $[112];
    }
    if ($[113] !== actionError) {
      t27 = actionError && <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">{actionError}</div>;
      $[113] = actionError;
      $[114] = t27;
    } else {
      t27 = $[114];
    }
    let t36;
    if ($[115] !== t) {
      t36 = t("email.channelHeading");
      $[115] = t;
      $[116] = t36;
    } else {
      t36 = $[116];
    }
    let t37;
    if ($[117] !== t36) {
      t37 = <h2 className="text-sm font-semibold uppercase text-muted-foreground">{t36}</h2>;
      $[117] = t36;
      $[118] = t37;
    } else {
      t37 = $[118];
    }
    let t38;
    if ($[119] !== status.mode) {
      t38 = modeVariant(status.mode);
      $[119] = status.mode;
      $[120] = t38;
    } else {
      t38 = $[120];
    }
    const t39 = `email.mode.${status.mode}`;
    let t40;
    if ($[121] !== t || $[122] !== t39) {
      t40 = t(t39);
      $[121] = t;
      $[122] = t39;
      $[123] = t40;
    } else {
      t40 = $[123];
    }
    let t41;
    if ($[124] !== t38 || $[125] !== t40) {
      t41 = <Badge variant={t38}>{t40}</Badge>;
      $[124] = t38;
      $[125] = t40;
      $[126] = t41;
    } else {
      t41 = $[126];
    }
    const t42 = `email.provider.${status.provider}`;
    let t43;
    if ($[127] !== status.provider || $[128] !== t || $[129] !== t42) {
      t43 = t(t42, {
        defaultValue: status.provider
      });
      $[127] = status.provider;
      $[128] = t;
      $[129] = t42;
      $[130] = t43;
    } else {
      t43 = $[130];
    }
    let t44;
    if ($[131] !== t43) {
      t44 = <span className="text-sm text-muted-foreground">{t43}</span>;
      $[131] = t43;
      $[132] = t44;
    } else {
      t44 = $[132];
    }
    let t45;
    if ($[133] !== t37 || $[134] !== t41 || $[135] !== t44) {
      t45 = <div className="flex flex-wrap items-center gap-3">{t37}{t41}{t44}</div>;
      $[133] = t37;
      $[134] = t41;
      $[135] = t44;
      $[136] = t45;
    } else {
      t45 = $[136];
    }
    let t46;
    if ($[137] !== status.note) {
      t46 = <p className="text-sm">{status.note}</p>;
      $[137] = status.note;
      $[138] = t46;
    } else {
      t46 = $[138];
    }
    let t47;
    if ($[139] !== act || $[140] !== canManage || $[141] !== status.pauseReason || $[142] !== status.pausedUntil || $[143] !== t) {
      t47 = status.pausedUntil && <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"><span>{t("email.paused", {
            until: formatDateTime(status.pausedUntil),
            reason: status.pauseReason
          })}</span>{canManage && <Button size="sm" variant="outline" onClick={() => act(_temp2)}>{t("email.resume")}</Button>}</div>;
      $[139] = act;
      $[140] = canManage;
      $[141] = status.pauseReason;
      $[142] = status.pausedUntil;
      $[143] = t;
      $[144] = t47;
    } else {
      t47 = $[144];
    }
    let t48;
    if ($[145] !== status.configErrors) {
      t48 = status.configErrors.map(_temp3);
      $[145] = status.configErrors;
      $[146] = t48;
    } else {
      t48 = $[146];
    }
    let t49;
    if ($[147] !== t) {
      t49 = t("email.from");
      $[147] = t;
      $[148] = t49;
    } else {
      t49 = $[148];
    }
    let t50;
    if ($[149] !== t49) {
      t50 = <span className="text-xs text-muted-foreground">{t49}</span>;
      $[149] = t49;
      $[150] = t50;
    } else {
      t50 = $[150];
    }
    let t51;
    if ($[151] !== status.from || $[152] !== status.fromName || $[153] !== t) {
      t51 = status.from ? `${status.fromName ? `${status.fromName} ` : ""}<${status.from}>` : t("email.notSet");
      $[151] = status.from;
      $[152] = status.fromName;
      $[153] = t;
      $[154] = t51;
    } else {
      t51 = $[154];
    }
    let t52;
    if ($[155] !== t51) {
      t52 = <span className="text-sm font-medium">{t51}</span>;
      $[155] = t51;
      $[156] = t52;
    } else {
      t52 = $[156];
    }
    let t53;
    if ($[157] !== t50 || $[158] !== t52) {
      t53 = <div className="flex flex-col gap-1">{t50}{t52}</div>;
      $[157] = t50;
      $[158] = t52;
      $[159] = t53;
    } else {
      t53 = $[159];
    }
    let t54;
    if ($[160] !== t) {
      t54 = t("email.server");
      $[160] = t;
      $[161] = t54;
    } else {
      t54 = $[161];
    }
    let t55;
    if ($[162] !== t54) {
      t55 = <span className="text-xs text-muted-foreground">{t54}</span>;
      $[162] = t54;
      $[163] = t55;
    } else {
      t55 = $[163];
    }
    const t56 = status.smtpHost ? `${status.smtpHost}:${status.smtpPort}` : status.apiUrl || "\u2014";
    let t57;
    if ($[164] !== t56) {
      t57 = <span className="text-sm font-medium">{t56}</span>;
      $[164] = t56;
      $[165] = t57;
    } else {
      t57 = $[165];
    }
    let t58;
    if ($[166] !== t55 || $[167] !== t57) {
      t58 = <div className="flex flex-col gap-1">{t55}{t57}</div>;
      $[166] = t55;
      $[167] = t57;
      $[168] = t58;
    } else {
      t58 = $[168];
    }
    let t59;
    if ($[169] !== t) {
      t59 = t("email.dailyLimit");
      $[169] = t;
      $[170] = t59;
    } else {
      t59 = $[170];
    }
    let t60;
    if ($[171] !== t59) {
      t60 = <span className="text-xs text-muted-foreground">{t59}</span>;
      $[171] = t59;
      $[172] = t60;
    } else {
      t60 = $[172];
    }
    let t61;
    if ($[173] !== status.dailyLimit || $[174] !== status.sentLast24h || $[175] !== t) {
      t61 = status.dailyLimit > 0 ? t("email.dailyUsage", {
        used: status.sentLast24h,
        limit: status.dailyLimit
      }) : t("email.noLimit");
      $[173] = status.dailyLimit;
      $[174] = status.sentLast24h;
      $[175] = t;
      $[176] = t61;
    } else {
      t61 = $[176];
    }
    let t62;
    if ($[177] !== t61) {
      t62 = <span className="text-sm font-medium">{t61}</span>;
      $[177] = t61;
      $[178] = t62;
    } else {
      t62 = $[178];
    }
    let t63;
    if ($[179] !== status.dailyLimit || $[180] !== usage) {
      t63 = status.dailyLimit > 0 && <div className="h-1.5 w-full rounded-full bg-muted"><div className={`h-1.5 rounded-full ${usage >= 90 ? "bg-destructive" : "bg-[#FA5C2E]"}`} style={{
          width: `${usage}%`
        }} /></div>;
      $[179] = status.dailyLimit;
      $[180] = usage;
      $[181] = t63;
    } else {
      t63 = $[181];
    }
    let t64;
    if ($[182] !== t60 || $[183] !== t62 || $[184] !== t63) {
      t64 = <div className="flex flex-col gap-1">{t60}{t62}{t63}</div>;
      $[182] = t60;
      $[183] = t62;
      $[184] = t63;
      $[185] = t64;
    } else {
      t64 = $[185];
    }
    let t65;
    if ($[186] !== t53 || $[187] !== t58 || $[188] !== t64) {
      t65 = <div className="grid gap-4 sm:grid-cols-3">{t53}{t58}{t64}</div>;
      $[186] = t53;
      $[187] = t58;
      $[188] = t64;
      $[189] = t65;
    } else {
      t65 = $[189];
    }
    let t66;
    if ($[190] !== t) {
      t66 = t("email.variables");
      $[190] = t;
      $[191] = t66;
    } else {
      t66 = $[191];
    }
    let t67;
    if ($[192] !== t66) {
      t67 = <span className="text-xs text-muted-foreground">{t66}</span>;
      $[192] = t66;
      $[193] = t67;
    } else {
      t67 = $[193];
    }
    let t68;
    if ($[194] !== status.variables || $[195] !== t) {
      let t69;
      if ($[197] !== t) {
        t69 = v => <Badge key={v.name} variant={v.set ? "secondary" : "outline"} className="font-mono">{v.set ? "\u2713" : "\u25CB"} {v.name}{v.secret ? ` · ${t("email.secret")}` : ""}</Badge>;
        $[197] = t;
        $[198] = t69;
      } else {
        t69 = $[198];
      }
      t68 = status.variables.map(t69);
      $[194] = status.variables;
      $[195] = t;
      $[196] = t68;
    } else {
      t68 = $[196];
    }
    let t69;
    if ($[199] !== t68) {
      t69 = <div className="flex flex-wrap gap-2">{t68}</div>;
      $[199] = t68;
      $[200] = t69;
    } else {
      t69 = $[200];
    }
    let t70;
    if ($[201] !== status.missingConfig || $[202] !== t) {
      t70 = status.missingConfig.length > 0 && <p className="text-sm text-destructive">{t("email.missing", {
          list: status.missingConfig.join(", ")
        })}</p>;
      $[201] = status.missingConfig;
      $[202] = t;
      $[203] = t70;
    } else {
      t70 = $[203];
    }
    let t71;
    if ($[204] !== status.provider || $[205] !== status.webhookUrl || $[206] !== t) {
      t71 = status.provider === "unisender" && status.webhookUrl && <p className="text-sm text-muted-foreground">{t("email.webhookHint", {
          url: status.webhookUrl
        })}</p>;
      $[204] = status.provider;
      $[205] = status.webhookUrl;
      $[206] = t;
      $[207] = t71;
    } else {
      t71 = $[207];
    }
    let t72;
    if ($[208] !== t67 || $[209] !== t69 || $[210] !== t70 || $[211] !== t71) {
      t72 = <div className="flex flex-col gap-2">{t67}{t69}{t70}{t71}</div>;
      $[208] = t67;
      $[209] = t69;
      $[210] = t70;
      $[211] = t71;
      $[212] = t72;
    } else {
      t72 = $[212];
    }
    let t73;
    if ($[213] !== stats) {
      t73 = <div className="grid gap-4 sm:grid-cols-5">{stats.map(_temp4)}</div>;
      $[213] = stats;
      $[214] = t73;
    } else {
      t73 = $[214];
    }
    let t74;
    if ($[215] !== status.stats.windowHours || $[216] !== t) {
      t74 = t("email.statsWindow", {
        count: status.stats.windowHours
      });
      $[215] = status.stats.windowHours;
      $[216] = t;
      $[217] = t74;
    } else {
      t74 = $[217];
    }
    let t75;
    if ($[218] !== t74) {
      t75 = <p className="text-xs text-muted-foreground">{t74}</p>;
      $[218] = t74;
      $[219] = t75;
    } else {
      t75 = $[219];
    }
    if ($[220] !== t45 || $[221] !== t46 || $[222] !== t47 || $[223] !== t48 || $[224] !== t65 || $[225] !== t72 || $[226] !== t73 || $[227] !== t75) {
      t28 = <section className="flex flex-col gap-4 rounded-lg border p-5" data-tour="email-status">{t45}{t46}{t47}{t48}{t65}{t72}{t73}{t75}</section>;
      $[220] = t45;
      $[221] = t46;
      $[222] = t47;
      $[223] = t48;
      $[224] = t65;
      $[225] = t72;
      $[226] = t73;
      $[227] = t75;
      $[228] = t28;
    } else {
      t28 = $[228];
    }
    if ($[229] !== canManage || $[230] !== runTest || $[231] !== status.live || $[232] !== t || $[233] !== testResult || $[234] !== testTo || $[235] !== testing) {
      t29 = canManage && <section className="flex flex-col gap-4 rounded-lg border p-5" data-tour="email-test"><h2 className="text-sm font-semibold uppercase text-muted-foreground">{t("email.testHeading")}</h2><p className="text-sm text-muted-foreground">{t("email.testHint")}</p><div className="flex flex-wrap items-end gap-3"><div className="flex flex-col gap-1.5"><Label htmlFor="email-test-to">{t("email.testTo")}</Label><Input id="email-test-to" type="email" placeholder="you@example.ru" value={testTo} onChange={e_0 => setTestTo(e_0.target.value)} className="w-72" /></div><Button onClick={runTest} disabled={testing || !testTo.includes("@")}>{testing ? t("email.testing") : t("email.runTest")}</Button></div>{testResult && <p className={`text-sm ${testResult.ok ? "text-muted-foreground" : "text-destructive"}`}>{testResult.ok ? status.live ? t("email.testOk") : t("email.testOkMock") : t("email.testFailed", {
            error: testResult.error || t(`email.status.${testResult.status}`, {
              defaultValue: testResult.status
            })
          })}</p>}</section>;
      $[229] = canManage;
      $[230] = runTest;
      $[231] = status.live;
      $[232] = t;
      $[233] = testResult;
      $[234] = testTo;
      $[235] = testing;
      $[236] = t29;
    } else {
      t29 = $[236];
    }
    t22 = "flex flex-col gap-4 rounded-lg border p-5";
    t23 = "email-templates";
    let t76;
    if ($[237] !== t) {
      t76 = t("email.templatesHeading");
      $[237] = t;
      $[238] = t76;
    } else {
      t76 = $[238];
    }
    let t77;
    if ($[239] !== t76) {
      t77 = <h2 className="text-sm font-semibold uppercase text-muted-foreground">{t76}</h2>;
      $[239] = t76;
      $[240] = t77;
    } else {
      t77 = $[240];
    }
    const t78 = previewAsText ? "outline" : "default";
    let t79;
    if ($[241] === Symbol.for("react.memo_cache_sentinel")) {
      t79 = () => setPreviewAsText(false);
      $[241] = t79;
    } else {
      t79 = $[241];
    }
    let t80;
    if ($[242] !== t78) {
      t80 = <Button size="sm" variant={t78} onClick={t79}>HTML</Button>;
      $[242] = t78;
      $[243] = t80;
    } else {
      t80 = $[243];
    }
    const t81 = previewAsText ? "default" : "outline";
    let t82;
    if ($[244] === Symbol.for("react.memo_cache_sentinel")) {
      t82 = () => setPreviewAsText(true);
      $[244] = t82;
    } else {
      t82 = $[244];
    }
    let t83;
    if ($[245] !== t) {
      t83 = t("email.textVersion");
      $[245] = t;
      $[246] = t83;
    } else {
      t83 = $[246];
    }
    let t84;
    if ($[247] !== t81 || $[248] !== t83) {
      t84 = <Button size="sm" variant={t81} onClick={t82}>{t83}</Button>;
      $[247] = t81;
      $[248] = t83;
      $[249] = t84;
    } else {
      t84 = $[249];
    }
    let t85;
    if ($[250] !== t80 || $[251] !== t84) {
      t85 = <div className="flex gap-1">{t80}{t84}</div>;
      $[250] = t80;
      $[251] = t84;
      $[252] = t85;
    } else {
      t85 = $[252];
    }
    if ($[253] !== t77 || $[254] !== t85) {
      t24 = <div className="flex flex-wrap items-center justify-between gap-3">{t77}{t85}</div>;
      $[253] = t77;
      $[254] = t85;
      $[255] = t24;
    } else {
      t24 = $[255];
    }
    t21 = "grid gap-4 lg:grid-cols-[280px_1fr]";
    t19 = "flex max-h-[700px] flex-col gap-4 overflow-y-auto pr-1";
    t20 = grouped.filter(_temp5).map(group => <div key={group.audience} className="flex flex-col gap-1"><span className="text-xs font-semibold uppercase text-muted-foreground">{t(`email.audience.${group.audience}`)}</span>{group.items.map(tpl_0 => <button key={tpl_0.id} type="button" onClick={() => setPreviewId(tpl_0.id)} className={`flex flex-col items-start rounded-md px-3 py-2 text-left text-sm transition-colors ${previewId === tpl_0.id ? "bg-muted font-medium" : "hover:bg-muted/60"}`}><span>{tpl_0.title}</span><span className="text-xs text-muted-foreground">{t(`email.category.${tpl_0.category}`)}</span></button>)}</div>);
    $[55] = act;
    $[56] = actionError;
    $[57] = canManage;
    $[58] = previewAsText;
    $[59] = previewId;
    $[60] = runTest;
    $[61] = stats;
    $[62] = status.apiUrl;
    $[63] = status.configErrors;
    $[64] = status.dailyLimit;
    $[65] = status.from;
    $[66] = status.fromName;
    $[67] = status.live;
    $[68] = status.missingConfig;
    $[69] = status.mode;
    $[70] = status.note;
    $[71] = status.pauseReason;
    $[72] = status.pausedUntil;
    $[73] = status.provider;
    $[74] = status.sentLast24h;
    $[75] = status.smtpHost;
    $[76] = status.smtpPort;
    $[77] = status.stats.windowHours;
    $[78] = status.variables;
    $[79] = status.webhookUrl;
    $[80] = t;
    $[81] = templates;
    $[82] = testResult;
    $[83] = testTo;
    $[84] = testing;
    $[85] = usage;
    $[86] = t19;
    $[87] = t20;
    $[88] = t21;
    $[89] = t22;
    $[90] = t23;
    $[91] = t24;
    $[92] = t25;
    $[93] = t26;
    $[94] = t27;
    $[95] = t28;
    $[96] = t29;
  } else {
    t19 = $[86];
    t20 = $[87];
    t21 = $[88];
    t22 = $[89];
    t23 = $[90];
    t24 = $[91];
    t25 = $[92];
    t26 = $[93];
    t27 = $[94];
    t28 = $[95];
    t29 = $[96];
  }
  let t30;
  if ($[256] !== t19 || $[257] !== t20) {
    t30 = <div className={t19}>{t20}</div>;
    $[256] = t19;
    $[257] = t20;
    $[258] = t30;
  } else {
    t30 = $[258];
  }
  let t31;
  if ($[259] !== preview || $[260] !== previewAsText || $[261] !== t) {
    t31 = <div className="flex min-w-0 flex-col gap-2">{preview ? <><div className="text-sm"><span className="text-muted-foreground">{t("email.subject")}: </span><span className="font-medium">{preview.subject}</span></div>{previewAsText ? <pre className="h-[640px] overflow-auto whitespace-pre-wrap rounded-md border bg-muted/30 p-4 text-sm">{preview.text}</pre> : <EmailFrame html={preview.html} title={preview.title} />}</> : <p className="text-sm text-muted-foreground">{t("email.loading")}</p>}</div>;
    $[259] = preview;
    $[260] = previewAsText;
    $[261] = t;
    $[262] = t31;
  } else {
    t31 = $[262];
  }
  let t32;
  if ($[263] !== t21 || $[264] !== t30 || $[265] !== t31) {
    t32 = <div className={t21}>{t30}{t31}</div>;
    $[263] = t21;
    $[264] = t30;
    $[265] = t31;
    $[266] = t32;
  } else {
    t32 = $[266];
  }
  let t33;
  if ($[267] !== t22 || $[268] !== t23 || $[269] !== t24 || $[270] !== t32) {
    t33 = <section className={t22} data-tour={t23}>{t24}{t32}</section>;
    $[267] = t22;
    $[268] = t23;
    $[269] = t24;
    $[270] = t32;
    $[271] = t33;
  } else {
    t33 = $[271];
  }
  let t34;
  if ($[272] !== t) {
    t34 = t("email.logHeading");
    $[272] = t;
    $[273] = t34;
  } else {
    t34 = $[273];
  }
  let t35;
  if ($[274] !== t34) {
    t35 = <h2 className="text-sm font-semibold uppercase text-muted-foreground">{t34}</h2>;
    $[274] = t34;
    $[275] = t35;
  } else {
    t35 = $[275];
  }
  let t36;
  if ($[276] !== t) {
    t36 = t("email.filterTo");
    $[276] = t;
    $[277] = t36;
  } else {
    t36 = $[277];
  }
  let t37;
  if ($[278] !== t36) {
    t37 = <Label htmlFor="email-log-to">{t36}</Label>;
    $[278] = t36;
    $[279] = t37;
  } else {
    t37 = $[279];
  }
  let t38;
  if ($[280] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = e_1 => setToFilter(e_1.target.value);
    $[280] = t38;
  } else {
    t38 = $[280];
  }
  let t39;
  if ($[281] !== toFilter) {
    t39 = <Input id="email-log-to" value={toFilter} onChange={t38} placeholder="@example.ru" />;
    $[281] = toFilter;
    $[282] = t39;
  } else {
    t39 = $[282];
  }
  let t40;
  if ($[283] !== t37 || $[284] !== t39) {
    t40 = <div className="flex flex-col gap-1.5">{t37}{t39}</div>;
    $[283] = t37;
    $[284] = t39;
    $[285] = t40;
  } else {
    t40 = $[285];
  }
  let t41;
  if ($[286] !== t) {
    t41 = t("email.filterTemplate");
    $[286] = t;
    $[287] = t41;
  } else {
    t41 = $[287];
  }
  let t42;
  if ($[288] !== t41) {
    t42 = <Label htmlFor="email-log-template">{t41}</Label>;
    $[288] = t41;
    $[289] = t42;
  } else {
    t42 = $[289];
  }
  let t43;
  if ($[290] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = e_2 => setTemplateFilter(e_2.target.value);
    $[290] = t43;
  } else {
    t43 = $[290];
  }
  let t44;
  if ($[291] !== t) {
    t44 = t("email.allTemplates");
    $[291] = t;
    $[292] = t44;
  } else {
    t44 = $[292];
  }
  let t45;
  if ($[293] !== t44) {
    t45 = <option value="all">{t44}</option>;
    $[293] = t44;
    $[294] = t45;
  } else {
    t45 = $[294];
  }
  let t46;
  if ($[295] !== templates) {
    t46 = templates ?? [];
    $[295] = templates;
    $[296] = t46;
  } else {
    t46 = $[296];
  }
  let t47;
  if ($[297] !== t46) {
    t47 = t46.map(_temp6);
    $[297] = t46;
    $[298] = t47;
  } else {
    t47 = $[298];
  }
  let t48;
  if ($[299] !== t45 || $[300] !== t47 || $[301] !== templateFilter) {
    t48 = <select id="email-log-template" value={templateFilter} onChange={t43} className="h-9 rounded-md border bg-background px-2 text-sm">{t45}{t47}</select>;
    $[299] = t45;
    $[300] = t47;
    $[301] = templateFilter;
    $[302] = t48;
  } else {
    t48 = $[302];
  }
  let t49;
  if ($[303] !== t42 || $[304] !== t48) {
    t49 = <div className="flex flex-col gap-1.5">{t42}{t48}</div>;
    $[303] = t42;
    $[304] = t48;
    $[305] = t49;
  } else {
    t49 = $[305];
  }
  let t50;
  if ($[306] !== t40 || $[307] !== t49) {
    t50 = <div className="flex flex-wrap items-end gap-3">{t40}{t49}</div>;
    $[306] = t40;
    $[307] = t49;
    $[308] = t50;
  } else {
    t50 = $[308];
  }
  let t51;
  if ($[309] !== t35 || $[310] !== t50) {
    t51 = <div className="flex flex-wrap items-end justify-between gap-3">{t35}{t50}</div>;
    $[309] = t35;
    $[310] = t50;
    $[311] = t51;
  } else {
    t51 = $[311];
  }
  let t52;
  if ($[312] !== statusFilter || $[313] !== t) {
    t52 = STATUS_FILTERS.map(s_0 => <Button key={s_0} size="sm" variant={statusFilter === s_0 ? "default" : "outline"} onClick={() => setStatusFilter(s_0)}>{t(`email.status.${s_0}`)}</Button>);
    $[312] = statusFilter;
    $[313] = t;
    $[314] = t52;
  } else {
    t52 = $[314];
  }
  let t53;
  if ($[315] !== t52) {
    t53 = <div className="flex flex-wrap gap-1">{t52}</div>;
    $[315] = t52;
    $[316] = t53;
  } else {
    t53 = $[316];
  }
  let t54;
  if ($[317] !== t) {
    t54 = t("email.logPrivacyNote");
    $[317] = t;
    $[318] = t54;
  } else {
    t54 = $[318];
  }
  let t55;
  if ($[319] !== t54) {
    t55 = <p className="text-xs text-muted-foreground">{t54}</p>;
    $[319] = t54;
    $[320] = t55;
  } else {
    t55 = $[320];
  }
  let t56;
  if ($[321] !== t) {
    t56 = t("email.table.time");
    $[321] = t;
    $[322] = t56;
  } else {
    t56 = $[322];
  }
  let t57;
  if ($[323] !== t56) {
    t57 = <th className="py-2 pr-4 font-medium">{t56}</th>;
    $[323] = t56;
    $[324] = t57;
  } else {
    t57 = $[324];
  }
  let t58;
  if ($[325] !== t) {
    t58 = t("email.table.to");
    $[325] = t;
    $[326] = t58;
  } else {
    t58 = $[326];
  }
  let t59;
  if ($[327] !== t58) {
    t59 = <th className="py-2 pr-4 font-medium">{t58}</th>;
    $[327] = t58;
    $[328] = t59;
  } else {
    t59 = $[328];
  }
  let t60;
  if ($[329] !== t) {
    t60 = t("email.table.template");
    $[329] = t;
    $[330] = t60;
  } else {
    t60 = $[330];
  }
  let t61;
  if ($[331] !== t60) {
    t61 = <th className="py-2 pr-4 font-medium">{t60}</th>;
    $[331] = t60;
    $[332] = t61;
  } else {
    t61 = $[332];
  }
  let t62;
  if ($[333] !== t) {
    t62 = t("email.table.subject");
    $[333] = t;
    $[334] = t62;
  } else {
    t62 = $[334];
  }
  let t63;
  if ($[335] !== t62) {
    t63 = <th className="py-2 pr-4 font-medium">{t62}</th>;
    $[335] = t62;
    $[336] = t63;
  } else {
    t63 = $[336];
  }
  let t64;
  if ($[337] !== t) {
    t64 = t("email.table.status");
    $[337] = t;
    $[338] = t64;
  } else {
    t64 = $[338];
  }
  let t65;
  if ($[339] !== t64) {
    t65 = <th className="py-2 pr-4 font-medium">{t64}</th>;
    $[339] = t64;
    $[340] = t65;
  } else {
    t65 = $[340];
  }
  let t66;
  if ($[341] !== t) {
    t66 = t("email.table.attempts");
    $[341] = t;
    $[342] = t66;
  } else {
    t66 = $[342];
  }
  let t67;
  if ($[343] !== t66) {
    t67 = <th className="py-2 pr-4 font-medium">{t66}</th>;
    $[343] = t66;
    $[344] = t67;
  } else {
    t67 = $[344];
  }
  let t68;
  if ($[345] === Symbol.for("react.memo_cache_sentinel")) {
    t68 = <th className="py-2 pr-4 font-medium" />;
    $[345] = t68;
  } else {
    t68 = $[345];
  }
  let t69;
  if ($[346] !== t57 || $[347] !== t59 || $[348] !== t61 || $[349] !== t63 || $[350] !== t65 || $[351] !== t67) {
    t69 = <thead className="text-left text-xs uppercase text-muted-foreground"><tr>{t57}{t59}{t61}{t63}{t65}{t67}{t68}</tr></thead>;
    $[346] = t57;
    $[347] = t59;
    $[348] = t61;
    $[349] = t63;
    $[350] = t65;
    $[351] = t67;
    $[352] = t69;
  } else {
    t69 = $[352];
  }
  let t70;
  if ($[353] !== log?.items) {
    t70 = log?.items ?? [];
    $[353] = log?.items;
    $[354] = t70;
  } else {
    t70 = $[354];
  }
  let t71;
  if ($[355] !== act || $[356] !== canManage || $[357] !== t || $[358] !== t70) {
    let t72;
    if ($[360] !== act || $[361] !== canManage || $[362] !== t) {
      t72 = item => <tr key={item.id} className="border-t align-top"><td className="py-2 pr-4 whitespace-nowrap text-muted-foreground">{formatDateTime(item.createdAt)}</td><td className="py-2 pr-4 whitespace-nowrap">{item.to}</td><td className="py-2 pr-4 text-muted-foreground">{item.templateTitle}</td><td className="py-2 pr-4">{item.subject}</td><td className="py-2 pr-4"><div className="flex flex-col gap-1"><Badge variant={statusVariant(item.status)}>{t(`email.status.${item.status}`, {
                defaultValue: item.status
              })}</Badge>{item.statusReason && <span className="text-xs text-muted-foreground">{t(`email.reason.${item.statusReason}`, {
                defaultValue: item.statusReason
              })}</span>}{item.lastError && <span className="max-w-xs text-xs text-destructive">{item.lastError}</span>}{item.providerStatus && <span className="text-xs text-muted-foreground">{t("email.providerStatus", {
                status: item.providerStatus
              })}</span>}</div></td><td className="py-2 pr-4 whitespace-nowrap text-muted-foreground">{item.attempts}/{item.maxAttempts}</td><td className="py-2 pr-4 whitespace-nowrap"><div className="flex gap-1"><Button size="sm" variant="outline" onClick={() => setOpenMessage(item.id)}>{t("email.open")}</Button>{canManage && item.status !== "sent" && item.status !== "delivered" && item.status !== "sending" && <Button size="sm" variant="outline" onClick={() => act(() => api.email.retry(item.id))}>{t("email.retry")}</Button>}</div></td></tr>;
      $[360] = act;
      $[361] = canManage;
      $[362] = t;
      $[363] = t72;
    } else {
      t72 = $[363];
    }
    t71 = t70.map(t72);
    $[355] = act;
    $[356] = canManage;
    $[357] = t;
    $[358] = t70;
    $[359] = t71;
  } else {
    t71 = $[359];
  }
  let t72;
  if ($[364] !== log?.items.length || $[365] !== t) {
    t72 = (log?.items.length ?? 0) === 0 && <tr><td colSpan={7} className="py-6 text-center text-muted-foreground">{t("email.logEmpty")}</td></tr>;
    $[364] = log?.items.length;
    $[365] = t;
    $[366] = t72;
  } else {
    t72 = $[366];
  }
  let t73;
  if ($[367] !== t71 || $[368] !== t72) {
    t73 = <tbody>{t71}{t72}</tbody>;
    $[367] = t71;
    $[368] = t72;
    $[369] = t73;
  } else {
    t73 = $[369];
  }
  let t74;
  if ($[370] !== t69 || $[371] !== t73) {
    t74 = <div className="overflow-x-auto"><table className="w-full text-sm">{t69}{t73}</table></div>;
    $[370] = t69;
    $[371] = t73;
    $[372] = t74;
  } else {
    t74 = $[372];
  }
  let t75;
  if ($[373] !== t51 || $[374] !== t53 || $[375] !== t55 || $[376] !== t74) {
    t75 = <section className="flex flex-col gap-4 rounded-lg border p-5" data-tour="email-log">{t51}{t53}{t55}{t74}</section>;
    $[373] = t51;
    $[374] = t53;
    $[375] = t55;
    $[376] = t74;
    $[377] = t75;
  } else {
    t75 = $[377];
  }
  const t76 = Boolean(openMessage);
  let t77;
  if ($[378] === Symbol.for("react.memo_cache_sentinel")) {
    t77 = open => !open && setOpenMessage(null);
    $[378] = t77;
  } else {
    t77 = $[378];
  }
  let t78;
  if ($[379] !== message?.subject || $[380] !== t) {
    t78 = message?.subject ?? t("email.loading");
    $[379] = message?.subject;
    $[380] = t;
    $[381] = t78;
  } else {
    t78 = $[381];
  }
  let t79;
  if ($[382] !== t78) {
    t79 = <DialogHeader><DialogTitle>{t78}</DialogTitle></DialogHeader>;
    $[382] = t78;
    $[383] = t79;
  } else {
    t79 = $[383];
  }
  let t80;
  if ($[384] !== message || $[385] !== t) {
    t80 = message && (message.html ? <EmailFrame html={message.html} title={message.subject} /> : <p className="text-sm text-muted-foreground">{t("email.bodyHidden")}</p>);
    $[384] = message;
    $[385] = t;
    $[386] = t80;
  } else {
    t80 = $[386];
  }
  let t81;
  if ($[387] !== t79 || $[388] !== t80) {
    t81 = <DialogContent className="max-w-3xl">{t79}{t80}</DialogContent>;
    $[387] = t79;
    $[388] = t80;
    $[389] = t81;
  } else {
    t81 = $[389];
  }
  let t82;
  if ($[390] !== t76 || $[391] !== t81) {
    t82 = <Dialog open={t76} onOpenChange={t77}>{t81}</Dialog>;
    $[390] = t76;
    $[391] = t81;
    $[392] = t82;
  } else {
    t82 = $[392];
  }
  let t83;
  if ($[393] !== t25 || $[394] !== t26 || $[395] !== t27 || $[396] !== t28 || $[397] !== t29 || $[398] !== t33 || $[399] !== t75 || $[400] !== t82) {
    t83 = <div className={t25}>{t26}{t27}{t28}{t29}{t33}{t75}{t82}</div>;
    $[393] = t25;
    $[394] = t26;
    $[395] = t27;
    $[396] = t28;
    $[397] = t29;
    $[398] = t33;
    $[399] = t75;
    $[400] = t82;
    $[401] = t83;
  } else {
    t83 = $[401];
  }
  return t83;
}
function _temp6(tpl_1) {
  return <option key={tpl_1.id} value={tpl_1.id}>{tpl_1.title}</option>;
}
function _temp5(g) {
  return g.items.length > 0;
}
function _temp4(stat) {
  return <div key={stat.label} className="rounded-md border p-3"><div className="text-xs text-muted-foreground">{stat.label}</div><div className="text-lg font-semibold">{stat.value}</div></div>;
}
function _temp3(err) {
  return <p key={err} className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">{err}</p>;
}
function _temp2() {
  return api.email.resume();
}
function _temp(s) {
  return s.user;
}